import { Fragment } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Dialog, DialogBackdrop, Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

import { Logo, IconSvg } from 'components/common'
import { SidebarDevBlock } from 'components/organisms/sidebar-dev-block'
import { getNavigationItems, getManageEventsNavigationItems } from 'utils/constants'

interface MainSidebarMobileProps {
	open: boolean
	onClose: () => void
}

export function MainSidebarMobile({ open, onClose }: MainSidebarMobileProps) {
	const navigationItems = getNavigationItems()
	const manageEventsItems = getManageEventsNavigationItems()
	const { pathname } = useLocation()
	const isManageEventsContext = pathname.startsWith('/manage/rep-clubs') || pathname.startsWith('/rep-club')

	const renderNavItem = (item: { name: string; href: string; icon: string; disabled: boolean }) => (
		<NavLink
			key={item.name}
			to={item.href}
			className={({ isActive }) =>
				clsx(
					'group flex flex-col items-center p-3 rounded-lg text-xl justify-center font-medium transition-[color,background-color] duration-hover ease-hover-ease',
					'text-white',
					{
						'text-primary bg-primary/10': isActive,
						'pointer-events-none text-white/10': item.disabled,
					}
				)
			}
			reloadDocument
			onClick={onClose}
		>
			{({ isActive }) => (
				<>
					<IconSvg
						icon={item.icon}
						className={clsx(
							'w-6 transition-colors duration-hover ease-hover-ease',
							isActive
								? 'text-primary'
								: 'text-secondary-text group-hover:text-white'
						)}
						aria-hidden="true"
					/>
					<span
						className={clsx(
							'transition-colors duration-hover ease-hover-ease',
							isActive && 'text-primary'
						)}
					>
						{item.name}
					</span>
				</>
			)}
		</NavLink>
	)

	return (
		<Transition show={open} as={Fragment}>
			<Dialog as="div" className="fixed inset-0 flex z-40 lg:hidden" onClose={onClose}>
				<TransitionChild
					as={Fragment}
					enter="transition-opacity ease-linear duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="transition-opacity ease-linear duration-300"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<DialogBackdrop className="fixed inset-0 bg-black/90" />
				</TransitionChild>
				<TransitionChild
					as={Fragment}
					enter="transition ease-in-out duration-300 transform"
					enterFrom="-translate-x-full"
					enterTo="translate-x-0"
					leave="transition ease-in-out duration-300 transform"
					leaveFrom="translate-x-0"
					leaveTo="-translate-x-full"
				>
					<div className="relative flex-1 flex flex-col w-full bg-primary-surface/[0.4] backdrop-blur-lg focus:outline-none">
						<TransitionChild
							as={Fragment}
							enter="ease-in-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in-out duration-300"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<div className="absolute bottom-6 right-1/2 -mr-6 pt-2">
								<button
									type="button"
									className="flex items-center justify-center h-12 w-12 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
									onClick={onClose}
								>
									<span className="sr-only">Close Sidebar</span>
									<XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
								</button>
							</div>
						</TransitionChild>
						<div className="flex-1 h-0 p-6">
							<div className="px-4 flex items-center justify-center">
								<a href="/">
									<Logo />
								</a>
							</div>
							<nav
								className="flex-1 mt-8 max-h-[calc(100vh-32px-56px-32px-56px)] overflow-auto"
								aria-label="Sidebar"
							>
								<div className="space-y-1">
									{navigationItems.map(renderNavItem)}
								</div>
								{isManageEventsContext && (
									<div className="space-y-1 mt-4 pt-4 border-t border-white/[0.08]">
										{manageEventsItems.map(renderNavItem)}
									</div>
								)}
								<SidebarDevBlock />
							</nav>
						</div>
					</div>
				</TransitionChild>
			</Dialog>
		</Transition>
	)
}
